/*
 * Copyright (c) Nexign, JSC, 2020-2023.
 */
/**
 * The component for select and search account
 *
 * @author Anton Panasenko
 * @author Aleksandr Perkhunov
 * @author Ilya Babanov
 * @version 1.23.0
 * @since 1.2.0
 */

import type { ChangeEvent, RefObject } from 'react';
import { Fragment, useEffect, useRef, useState } from 'react';

import debounce from 'lodash/debounce';
import get from 'lodash/get';
import invoke from 'lodash/invoke';

import { icons } from '@assets/index';
import {
  DEBOUNCE_DEFAULT_DELAY,
  KeyboardKeyEnum,
} from '@modules/app/appConstants';
import type ICommonTranslation from '@modules/internationalization/types/common';

import { Input } from '../common';
import { EInputCursor } from '../common/Input/Input';
import { AccountSelectDisplay } from './AccountSelectDisplay';
import { AccountSelectList } from './AccountSelectList';
import type { IAccountSelectItem } from './AccountSelectTypes';
import {
  AccountSelectButtonContainerStyled,
  AccountSelectContainerStyled,
  AccountSelectLabelStyled,
  AccountSelectListEmptyStyled,
  AccountSelectLoaderStyled,
} from './styled/AccountSelectStyled';

export interface IAccountSelectProps {
  accountList: IAccountSelectItem[];
  dataNameComponent: string;
  disabled?: boolean;
  isValid?: boolean;
  isLoading?: boolean;
  hasMore: boolean;
  label?: string;
  onAccountSelect: (account: IAccountSelectItem) => void;
  onOpenList?: () => void;
  onScrollAccountList: () => void;
  onStartSearch: (value?: string) => void;
  selectPlaceholder: string;
  searchValue: string;
  selectedAccount: IAccountSelectItem;
  setSearchValue: (value: string) => void;
  translation: ICommonTranslation;
}

const AccountSelect = (props: IAccountSelectProps) => {
  const [isOpened, setOpened] = useState(false);
  const containerRef: RefObject<HTMLDivElement> = useRef(null);

  useEffect(() => {
    window.addEventListener('click', handleClickOutside, true);
    window.addEventListener('keydown', windowKeyDownHandler, true);
    return () => {
      window.removeEventListener('click', handleClickOutside, true);
      window.removeEventListener('keydown', windowKeyDownHandler, true);
    };
  }, []);

  const delayedStartSearch = debounce((value: string) => {
    props.onStartSearch(value);
  }, DEBOUNCE_DEFAULT_DELAY);

  const handleClickOutside = (event: MouseEvent): void => {
    if (
      get(containerRef, 'current') &&
      !invoke(containerRef, 'current.contains', event.target as Node)
    ) {
      setOpened(false);
    }
  };

  const windowKeyDownHandler = (event: KeyboardEvent): void => {
    if (event.key === KeyboardKeyEnum.ESCAPE) {
      setOpened(false);
    }
  };

  const handleAccountSelect = (account: IAccountSelectItem) => {
    props.onAccountSelect(account);
    setOpened(false);
  };

  const handleClick = () => {
    if (props.disabled) return;

    if (!isOpened) {
      props.onOpenList && props.onOpenList();
      setOpened(true);
    }
  };

  const handleChange = (event: ChangeEvent<HTMLInputElement>): void => {
    const searchValue = event.target.value ?? '';
    if (/\D/.test(searchValue)) {
      event.preventDefault();
      return;
    }
    props.setSearchValue(searchValue);
    delayedStartSearch(searchValue);
  };

  return (
    <AccountSelectContainerStyled
      data-component={props.dataNameComponent}
      ref={containerRef}
    >
      <AccountSelectButtonContainerStyled onClick={handleClick}>
        {isOpened && (
          <Fragment>
            <Input
              autoFocus={isOpened}
              autoSelect={true}
              cursor={EInputCursor.pointer}
              data-name="accountSelectInputToSearch"
              label={props.label}
              placeholder={props.selectPlaceholder}
              righticon={props.isLoading ? undefined : icons.searchMediumIcon}
              value={props.searchValue || ''}
              onChange={handleChange}
            />
            {props.isLoading && <AccountSelectLoaderStyled />}
          </Fragment>
        )}
        {!isOpened && (
          <Fragment>
            {props.label && (
              <AccountSelectLabelStyled>{props.label}</AccountSelectLabelStyled>
            )}
            <AccountSelectDisplay
              currency={props.translation.currency}
              data-name="accountSelectDisplay"
              defaultValue={props.selectPlaceholder}
              disabled={props.disabled ?? false}
              isLoading={props.isLoading ?? false}
              isValid={props.isValid}
              selectedAccount={props.selectedAccount}
            />
          </Fragment>
        )}
      </AccountSelectButtonContainerStyled>

      {isOpened && (
        <Fragment>
          {props.accountList && props.accountList.length > 0 ? (
            <AccountSelectList
              data={props.accountList}
              data-name="accountSelectList"
              hasMore={props.hasMore}
              searchValue={props.searchValue}
              translation={props.translation}
              onScrollAccountList={props.onScrollAccountList}
              onSelectAccount={handleAccountSelect}
            />
          ) : (
            !props.isLoading && (
              <AccountSelectListEmptyStyled>
                {props.translation.accountSelect.notFound}
              </AccountSelectListEmptyStyled>
            )
          )}
        </Fragment>
      )}
    </AccountSelectContainerStyled>
  );
};

AccountSelect.defaultProps = {
  dataNameComponent: 'accountSelect',
  isValid: true,
  searchValue: '',
  selectedAccount: {
    id: '',
    balance: 0,
    name: '',
  },
};

export default AccountSelect;
